import {
  MAX_NUMBER_OF_CUSTOM_LABELS,
  SELF_HOSTED_LABEL,
  SYSTEM_LABELS,
} from 'src/components/pipelines/models';

import { INode, Options } from '../types';

import { DEFAULT_MAX_STEP_SIZE_FOR_SELF_HOSTED_RUNNERS } from './const';
import { createEntityError, EntityError } from './entities';
import { ParsingState } from './parsingState';
import {
  choiceWord,
  getAllowedSizes,
  getEffectiveMaxAllowedSizeForCloudRunners,
  joinChoices,
} from './util';

const isSelfHostedRunnerStep = (step: INode) => {
  let isSelfHosted = false;

  if (Array.isArray(step.result['runs-on'])) {
    // label list
    isSelfHosted = step.result['runs-on'].includes(SELF_HOSTED_LABEL);
  } else if (
    step.result['runs-on'] !== null ||
    step.result['runs-on'] !== undefined
  ) {
    // inline label
    isSelfHosted = step.result['runs-on'] === SELF_HOSTED_LABEL;
  }
  return isSelfHosted;
};

export const stepFilter = (options?: Options) => {
  return (
    step: INode,
    key: string,
    node: INode,
    parsingState: ParsingState
  ): EntityError[] | null => {
    const envDepError = environmentDeploymentFilter(step, node);
    if (envDepError) {
      return envDepError;
    }
    switch (key) {
      case 'services':
        return stepServiceFilter(step, node, parsingState);
      case 'runs-on':
        return stepRunsOnFilter(step, node);
      case 'caches':
        return stepCachesFilter(step, node, parsingState);
      case 'clone':
        return stepCloneFilter(step, node);
      case 'size':
        return stepSizeFilter(step, node, options);
      default:
        return null;
    }
  };
};

function environmentDeploymentFilter(
  entity: INode,
  node: INode
): EntityError[] | null {
  if (entity.result.environment && entity.result.deployment) {
    return createEntityError(
      node,
      `'environment' and 'deployment' cannot be specified together.`
    );
  }
  return null;
}

function stepServiceFilter(
  step: INode,
  node: INode,
  parsingState: ParsingState
): EntityError[] {
  let entityError: any = null;
  if (
    step.result.services !== undefined &&
    step.result.services !== null &&
    Array.isArray(step.result.services)
  ) {
    const servicesMemory = parsingState.getSumOfServicesMemory(
      step.result.services
    );
    const buildContainerMemoryLimit = parsingState.getBuildContainerMemoryLimit(
      step.result.size
    );

    if (buildContainerMemoryLimit - servicesMemory < 1024) {
      entityError = createEntityError(
        node,
        `You've exceeded the services memory limit of ${buildContainerMemoryLimit}. Try reducing it.`
      );
    }
  }
  return entityError;
}

function stepRunsOnFilter(step: INode, node: INode): EntityError[] {
  let entityError: any = null;
  if (
    step.result['runs-on'] !== undefined &&
    step.result['runs-on'] !== null &&
    Array.isArray(step.result['runs-on'])
  ) {
    // Check that there are only 10 labels, other than 'self.hosted' and system label
    const runnerLabels = step.result['runs-on'].filter(
      label => label !== SELF_HOSTED_LABEL && !SYSTEM_LABELS.includes(label)
    );

    // Check that runs-on contains only one system label
    const systemLabels = step.result['runs-on'].filter(value =>
      SYSTEM_LABELS.includes(value)
    );

    if (systemLabels?.length > 1) {
      entityError = createEntityError(
        node,
        `There can only be 1 system label.`
      );
    }

    if (runnerLabels.length > MAX_NUMBER_OF_CUSTOM_LABELS) {
      entityError = createEntityError(
        node,
        `You have reached the ${MAX_NUMBER_OF_CUSTOM_LABELS} custom label limit.`
      );
    }
  }
  return entityError;
}

function stepCachesFilter(
  step: INode,
  node: INode,
  parsingState: ParsingState
): EntityError[] {
  let entityError: any = null;
  if (step.result.caches !== undefined && step.result.caches !== null) {
    const hasDockerService =
      parsingState.getGlobalOptionsDocker() ||
      (step.result.services !== undefined &&
      step.result.services !== null &&
      Array.isArray(step.result.services)
        ? step.result.services.includes('docker') ||
          step.result.services.some(
            (service: string) =>
              parsingState.getDefinitions()?.services?.[service]?.type ===
              'docker'
          )
        : false);

    const hasDockerCache =
      Array.isArray(step.result.caches) &&
      step.result.caches.includes('docker');

    if (hasDockerCache && !hasDockerService) {
      entityError = createEntityError(
        node,
        `It looks like docker isn't defined as a service in this step. ` +
          `You'll need to add that before you can add a docker cache.`
      );
    }
  }
  return entityError;
}

function stepCloneFilter(step: INode, node: INode): EntityError[] {
  let entityError: any = null;
  if (step.result.clone !== null && step.result.clone !== undefined) {
    if (step.result.clone['skip-ssl-verify'] && !isSelfHostedRunnerStep(step)) {
      entityError = createEntityError(
        node,
        `Skipping ssl certificate verification is only allowed on steps running on runners using the self.hosted label.`
      );
    }
  }
  return entityError;
}

function stepSizeFilter(
  step: INode,
  node: INode,
  options?: Options
): EntityError[] {
  let entityError: any = null;

  const effectiveMaxAllowedSizeForCloudRunners =
    getEffectiveMaxAllowedSizeForCloudRunners(options);

  if (step.result.size !== null && step.result.size !== undefined) {
    if (isSelfHostedRunnerStep(step)) {
      const sizeRange = getAllowedSizes(
        DEFAULT_MAX_STEP_SIZE_FOR_SELF_HOSTED_RUNNERS
      );
      if (!sizeRange.includes(step.result.size)) {
        const sizeOptions = choiceWord(sizeRange) + joinChoices(sizeRange);
        entityError = createEntityError(
          node,
          `Use ${sizeOptions} as the string`
        );
      }
    } else {
      const sizeRange = getAllowedSizes(effectiveMaxAllowedSizeForCloudRunners);
      if (!sizeRange.includes(step.result.size)) {
        const sizeOptions = choiceWord(sizeRange) + joinChoices(sizeRange);
        entityError = createEntityError(
          node,
          `Use ${sizeOptions} as the string`
        );
      }
    }
  }
  return entityError;
}

export const stageFilter = () => {
  return (
    stage: INode,
    key: string,
    node: INode,
    parsingState: ParsingState
  ): EntityError[] | null => {
    let entityError = null;

    const envDepError = environmentDeploymentFilter(stage, node);
    if (envDepError) {
      entityError = envDepError;
    }
    if (
      key === 'name' &&
      stage.result.name !== undefined &&
      stage.result.name !== null
    ) {
      const isStageNameUnique =
        parsingState
          .getStages()
          .filter((obj: any) => obj.name === stage.result.name).length <= 1;
      if (!isStageNameUnique) {
        entityError = createEntityError(
          node,
          `A stage name must be unique per pipeline.`
        );
      }
    } else if (
      key === 'steps' &&
      stage.result.steps !== undefined &&
      stage.result.steps !== null
    ) {
      const stageTrigger = stage.result.trigger;
      const firstStepNode = node.children[0];
      const firstStepTrigger = firstStepNode?.result?.step?.trigger;
      if (
        stageTrigger &&
        firstStepTrigger &&
        stageTrigger !== firstStepTrigger
      ) {
        entityError = createEntityError(
          firstStepNode,
          `The first step inside of a stage cannot override the trigger of that stage.`
        );
      }
    }
    return entityError;
  };
};

export const serviceFilter = (node: INode): EntityError[] | null => {
  if (node.result.length > 5) {
    return createEntityError(node, `You need less than 5 services in a step.`);
  }

  return null;
};
